import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Users, DollarSign, Edit2, Trash2, UserPlus, Search as SearchIcon, Receipt } from 'lucide-react';
import DashboardLayout from '../../layouts/DashboardLayout';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../../context/ToastContext';
import { DeudaService } from '../../../application/services/DeudaService';
import { AdminService } from '../../../application/services/AdminService';
import moment from 'moment-timezone';

const DeudasPage = () => {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [selectedEvento, setSelectedEvento] = useState(null);
    const [detalles, setDetalles] = useState([]);
    const [loadingDetalles, setLoadingDetalles] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showAbonosModal, setShowAbonosModal] = useState(false);
    const [selectedAbonosDetalle, setSelectedAbonosDetalle] = useState(null);

    // Edit states for individual assignment
    const [editingDetalle, setEditingDetalle] = useState(null);
    const [editValue, setEditValue] = useState('');

    const [formData, setFormData] = useState({
        titulo: '',
        descripcion: '',
        valor_default: '',
        fecha_evento: moment().format('YYYY-MM-DD')
    });

    const toast = useToast();

    const loadEventos = useCallback(async () => {
        setLoading(true);
        try {
            const data = await DeudaService.getEventos();
            setEventos(data || []);
        } catch (err) {
            toast.error('Error al cargar eventos de deuda');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadEventos();
    }, [loadEventos]);

    const handleOpenCreate = () => {
        setIsEditing(false);
        setFormData({
            titulo: '',
            descripcion: '',
            valor_default: '',
            fecha_evento: moment().format('YYYY-MM-DD')
        });
        setShowModal(true);
    };

    const handleOpenEdit = (ev) => {
        setIsEditing(true);
        setSelectedEvento(ev);
        setFormData({
            titulo: ev.titulo,
            descripcion: ev.descripcion,
            valor_default: ev.valor_default,
            fecha_evento: moment(ev.fecha_evento).format('YYYY-MM-DD')
        });
        setShowModal(true);
    };

    const handleSaveEvento = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (isEditing) {
                await DeudaService.updateEvento(selectedEvento.id_evento, formData);
                toast.success('Evento actualizado exitosamente');
            } else {
                await DeudaService.createEvento(formData);
                toast.success('Evento creado exitosamente');
            }
            setShowModal(false);
            loadEventos();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar evento');
        } finally {
            setIsSaving(false);
        }
    };

    const handleViewDetails = async (evento) => {
        setSelectedEvento(evento);
        setShowDetails(true);
        setLoadingDetalles(true);
        try {
            const data = await DeudaService.getDetallesEvento(evento.id_evento);
            setDetalles(data || []);
        } catch (err) {
            toast.error('Error al cargar detalles del evento');
        } finally {
            setLoadingDetalles(false);
        }
    };

    const handleUnassign = async (idDetalle) => {
        if (!window.confirm('¿Eliminar esta asignación?')) return;
        try {
            await DeudaService.deleteAsignacion(idDetalle);
            setDetalles(detalles.filter(d => d.id_deuda_detalle !== idDetalle));
            toast.success('Asignación eliminada');
            loadEventos(); // Update count in main table
        } catch (err) {
            toast.error('Error al eliminar asignación');
        }
    };

    const handleDeleteEvento = async (evento) => {
        if (!window.confirm(`¿Estás seguro de eliminar el evento "${evento.titulo}"? Esta acción eliminará también sus asignaciones si no han sido pagadas.`)) return;
        try {
            await DeudaService.deleteEvento(evento.id_evento);
            toast.success('Evento de cobro eliminado con éxito');
            loadEventos();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Error al eliminar el evento';
            toast.error(msg);
        }
    };

    const handleStartEditDetalle = (detalle) => {
        setEditingDetalle(detalle.id_deuda_detalle);
        setEditValue(detalle.valor_total);
    };

    const handleSaveDetalleEdit = async (idDetalle) => {
        try {
            await DeudaService.updateAsignacion(idDetalle, { valor_total: editValue });
            setDetalles(detalles.map(d => d.id_deuda_detalle === idDetalle ? { ...d, valor_total: parseFloat(editValue) } : d));
            setEditingDetalle(null);
            toast.success('Valor actualizado');
        } catch (err) {
            toast.error('Error al actualizar valor');
        }
    };

    const handleSearchClient = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (query.length < 3) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        try {
            const data = await AdminService.searchClientes(query);
            setSearchResults(data.items || data || []);
        } catch (err) {
            console.error('Error searching clients:', err);
        } finally {
            setSearching(false);
        }
    };

    const handleAsignarIndividual = async (clienteId) => {
        setIsSaving(true);
        try {
            await DeudaService.asignarIndividual(selectedEvento.id_evento, clienteId);
            toast.success(`Deuda asignada correctamente`);
            setShowAssignModal(false);
            loadEventos();
            if (showDetails) handleViewDetails(selectedEvento);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al asignar deuda individual');
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return moment(dateStr).format('D/M/YYYY');
    };

    return (
        <DashboardLayout title="Deudas por Cobrar (Extras)">
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Cuentas por Cobrar (Extras)</h1>
                        <p className="text-sm text-gray-500 mt-1">Gestione multas, aportes y otros cobros fuera del consumo de agua.</p>
                    </div>
                    <Button 
                        onClick={handleOpenCreate}
                        className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        Nuevo Evento de Cobro
                    </Button>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                    <th className="px-4 py-3">Fecha</th>
                                    <th className="px-4 py-3">Concepto</th>
                                    <th className="px-4 py-3 text-center">Asignados</th>
                                    <th className="px-4 py-3 text-center">Valor Base</th>
                                    <th className="px-4 py-3">Descripción</th>
                                    <th className="px-4 py-3 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            <div className="flex justify-center items-center">
                                                <Plus className="w-5 h-5 animate-spin mr-2" />
                                                Cargando eventos...
                                            </div>
                                        </td>
                                    </tr>
                                ) : eventos.length === 0 ? (
                                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No hay eventos registrados.</td></tr>
                                ) : eventos.map((ev) => (
                                    <tr key={ev.id_evento} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {formatDate(ev.fecha_evento)}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900">
                                            {ev.titulo}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                                                <Users className="w-3 h-3 mr-1" />
                                                {ev.total_asignados || 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">
                                            ${parseFloat(ev.valor_default || 0).toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">
                                            {ev.descripcion || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2 text-gray-400">
                                                <button 
                                                    title="Editar Evento"
                                                    onClick={() => handleOpenEdit(ev)}
                                                    className="hover:text-blue-600 transition-colors p-1"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    title="Asignar Individual"
                                                    onClick={() => {
                                                        setSelectedEvento(ev);
                                                        setShowAssignModal(true);
                                                        setSearchQuery('');
                                                        setSearchResults([]);
                                                    }}
                                                    className="hover:text-green-600 transition-colors p-1"
                                                >
                                                    <UserPlus className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    title="Eliminar Evento"
                                                    onClick={() => handleDeleteEvento(ev)}
                                                    className="hover:text-red-600 transition-colors p-1"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    title="Nómina"
                                                    onClick={() => handleViewDetails(ev)}
                                                    className="bg-gray-700 text-white hover:bg-gray-800 transition-all rounded px-2 py-1 text-xs flex items-center gap-1 ml-1"
                                                >
                                                    <Search className="w-3 h-3" />
                                                    Nómina
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal de Creación / Edición */}
            <Modal 
                isOpen={showModal} 
                onClose={() => setShowModal(false)} 
                title={isEditing ? 'Editar Evento' : 'Nuevo Evento de Cobro'}
                size="lg"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" onClick={handleSaveEvento} isLoading={isSaving}>
                            {isEditing ? 'Guardar Cambios' : 'Crear Evento'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Título del Evento</label>
                        <input 
                            required 
                            placeholder="Ej: Minga General, Aportes, etc."
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none"
                            value={formData.titulo}
                            onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Valor Cuesta ($)</label>
                            <input 
                                type="number" 
                                step="0.01" 
                                required
                                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none"
                                value={formData.valor_default}
                                onChange={(e) => setFormData({...formData, valor_default: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fecha</label>
                            <input 
                                type="date" 
                                required
                                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                                value={formData.fecha_evento}
                                onChange={(e) => setFormData({...formData, fecha_evento: e.target.value})}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descripción</label>
                        <textarea 
                            rows={2}
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none"
                            value={formData.descripcion}
                            onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                        />
                    </div>
                </div>
            </Modal>

            {/* Modal de Detalles y Gestión de Nómina */}
            <Modal 
                isOpen={showDetails} 
                onClose={() => setShowDetails(false)} 
                title={`Gestión de Nómina: ${selectedEvento?.titulo}`}
                size="xl"
            >
                <div className="pt-0">
                    <div className="bg-blue-50/50 p-4 rounded-lg flex items-center justify-between mb-4 border border-blue-100">
                        <div>
                            <span className="text-[10px] font-bold text-blue-600 uppercase block tracking-wider">Valor Referencia</span>
                            <span className="text-xl font-bold text-blue-900">${parseFloat(selectedEvento?.valor_default || 0).toFixed(2)}</span>
                        </div>
                        <div className="text-right">
                            <span className="text-[10px] font-bold text-blue-600 uppercase block tracking-wider">Total Asignados</span>
                            <span className="text-xl font-bold text-blue-900">{detalles.length} Clientes</span>
                        </div>
                    </div>

                    {loadingDetalles ? (
                        <div className="text-center py-12"><Plus className="w-6 h-6 animate-spin mx-auto text-blue-500" /></div>
                    ) : detalles.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-100">
                            <span className="text-gray-400">No hay clientes asignados a este evento aún.</span>
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                            <table className="w-full text-left border-collapse text-sm">
                                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[10px] tracking-wider">
                                    <tr>
                                        <th className="px-4 py-3">Cliente</th>
                                        <th className="px-4 py-3 text-center">Total Asignado</th>
                                        <th className="px-4 py-3 text-center">Abonado</th>
                                        <th className="px-4 py-3 text-center">Saldo Pendiente</th>
                                        <th className="px-4 py-3 text-center">Estado</th>
                                        <th className="px-4 py-3 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {detalles.map(d => (
                                        <tr key={d.id_deuda_detalle} className={`hover:bg-gray-50 transition-colors ${d.estado_pago === 'Pagado' ? 'bg-green-50/20' : ''}`}>
                                            <td className="px-4 py-3">
                                                <div className="font-bold text-gray-900">
                                                    {d.cliente ? `${d.cliente.nombres} ${d.cliente.apellidos}` : '---'}
                                                </div>
                                                <div className="text-[10px] font-medium text-gray-400">CÓD: {d.cliente?.codigo_cliente}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {editingDetalle === d.id_deuda_detalle ? (
                                                    <div className="flex items-center gap-1 justify-center">
                                                        <input 
                                                            type="number" 
                                                            step="0.01"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            className="w-20 px-2 py-1 border rounded text-right font-bold text-blue-700 outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                        <button 
                                                            onClick={() => handleSaveDetalleEdit(d.id_deuda_detalle)}
                                                            className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                                        >
                                                            <Plus className="w-4 h-4 rotate-45" />
                                                            ✓
                                                        </button>
                                                        <button 
                                                            onClick={() => setEditingDetalle(null)}
                                                            className="p-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="font-bold text-gray-900">${parseFloat(d.valor_total).toFixed(2)}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-green-700">${parseFloat(d.valor_pagado || 0).toFixed(2)}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-bold text-red-600">${parseFloat(d.valor_pendiente || (d.valor_total - d.valor_pagado) || 0).toFixed(2)}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                                                    d.estado_pago === 'Pagado' ? 'bg-green-100 text-green-700' : 
                                                    d.estado_pago === 'Parcial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {d.estado_pago.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2 text-gray-400">
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedAbonosDetalle(d);
                                                            setShowAbonosModal(true);
                                                        }}
                                                        className="hover:text-green-600 transition-colors p-1"
                                                        title="Ver Historial de Abonos / Cuotas"
                                                    >
                                                        <Receipt className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        disabled={d.estado_pago === 'Pagado'} 
                                                        onClick={() => handleStartEditDetalle(d)}
                                                        className="hover:text-blue-600 disabled:opacity-30 transition-colors p-1"
                                                        title="Editar Monto Asignado"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        disabled={d.valor_pagado > 0} 
                                                        onClick={() => handleUnassign(d.id_deuda_detalle)}
                                                        className="hover:text-red-600 disabled:opacity-30 transition-colors p-1"
                                                        title="Quitar de Nómina"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Modal para Asignación Individual */}
            <Modal 
                isOpen={showAssignModal} 
                onClose={() => setShowAssignModal(false)} 
                title={`Asignar Cliente: ${selectedEvento?.titulo}`}
                size="lg"
            >
                <div className="space-y-4 pt-0">
                    <div className="bg-amber-50 p-3 rounded-lg flex items-center gap-3 text-amber-900 border border-amber-100">
                        <DollarSign className="text-amber-600 w-5 h-5" />
                        <span className="text-xs font-medium uppercase tracking-tight">Valor a asignar: <strong>${parseFloat(selectedEvento?.valor_default || 0).toFixed(2)}</strong></span>
                    </div>
                    
                    <div className="relative">
                        <input 
                            placeholder="Buscar por Nombre o Cédula..."
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm transition-all shadow-sm"
                            value={searchQuery}
                            onChange={handleSearchClient}
                        />
                        <SearchIcon className="absolute left-3.5 top-2.5 w-4 h-4 text-gray-400" />
                    </div>

                    <div className="max-h-[300px] overflow-y-auto pr-1 divide-y divide-gray-100 border border-gray-100 rounded-lg">
                        {searching ? (
                            <div className="text-center py-8"><Plus className="w-6 h-6 animate-spin mx-auto text-blue-400" /></div>
                        ) : searchQuery.length < 3 ? (
                            <div className="text-center py-8 text-gray-400 text-xs italic">Escribe al menos 3 caracteres para buscar</div>
                        ) : searchResults.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 text-xs">No se encontraron clientes</div>
                        ) : searchResults.map(cliente => (
                            <div key={cliente.id_cliente} className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors">
                                <div>
                                    <div className="font-bold text-gray-900 text-sm">
                                        {cliente.nombres} {cliente.apellidos}
                                    </div>
                                    <div className="text-[10px] font-bold text-gray-400 tracking-wider">CÉDULA: {cliente.codigo_cliente}</div>
                                </div>
                                <Button 
                                    className="py-1 px-3 text-xs"
                                    onClick={() => handleAsignarIndividual(cliente.id_cliente)}
                                    isLoading={isSaving}
                                >
                                    Asignar
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>

            {/* Modal de Historial de Abonos / Cuotas */}
            <Modal
                isOpen={showAbonosModal}
                onClose={() => setShowAbonosModal(false)}
                title={`Historial de Abonos: ${selectedAbonosDetalle?.cliente ? `${selectedAbonosDetalle.cliente.nombres} ${selectedAbonosDetalle.cliente.apellidos}` : 'Cliente'}`}
                size="lg"
                footer={
                    <Button variant="secondary" onClick={() => setShowAbonosModal(false)}>Cerrar</Button>
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3 bg-blue-50/40 p-3 rounded-lg border border-blue-100 text-center">
                        <div>
                            <span className="text-[10px] font-bold text-gray-500 uppercase block tracking-wider">Total Deuda</span>
                            <span className="text-base font-bold text-gray-900">${parseFloat(selectedAbonosDetalle?.valor_total || 0).toFixed(2)}</span>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-green-600 uppercase block tracking-wider">Total Abonado</span>
                            <span className="text-base font-bold text-green-700">${parseFloat(selectedAbonosDetalle?.valor_pagado || 0).toFixed(2)}</span>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-red-600 uppercase block tracking-wider">Saldo Pendiente</span>
                            <span className="text-base font-bold text-red-700">${parseFloat(selectedAbonosDetalle?.valor_pendiente || (selectedAbonosDetalle?.valor_total - selectedAbonosDetalle?.valor_pagado) || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead className="bg-gray-100 text-gray-600 font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2">Fecha y Hora</th>
                                    <th className="px-3 py-2 text-center">N° Comprobante</th>
                                    <th className="px-3 py-2 text-center">Monto Abonado</th>
                                    <th className="px-3 py-2 text-right">Recaudador</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {selectedAbonosDetalle?.abonos && selectedAbonosDetalle.abonos.length > 0 ? (
                                    selectedAbonosDetalle.abonos.map((abono, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 font-medium text-gray-700">
                                                {moment(abono.fecha_pago).format('DD/MM/YYYY HH:mm')}
                                            </td>
                                            <td className="px-3 py-2 text-center font-mono font-bold text-blue-600">
                                                {abono.numero_comprobante || '---'}
                                            </td>
                                            <td className="px-3 py-2 text-center font-bold text-green-700">
                                                ${parseFloat(abono.monto_pagado || 0).toFixed(2)}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-500">
                                                {abono.nombre_tesorero || 'Tesorero/a'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-4 py-6 text-center text-gray-400 italic">
                                            No se registran abonos realizados para esta deuda aún.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
        </DashboardLayout>
    );
};

export default DeudasPage;
